import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/billing/stripe';

// The raw card details handed to Stripe server-side. `expiry` is the combined
// MM/YY string the backend splits; `verificationNumbers` is the CVC. Keys are
// camelCase — the backend underscores request keys before permitting them.
export const UpdateStripeCardVariablesSchema = z.object({
  bankAccountNumber: z.string().min(1),
  holderName: z.string().min(1),
  expiry: z.string().regex(/^\d{2}\/\d{2}$/),
  verificationNumbers: z.string().min(1),
});

export type UpdateStripeCardVariables = z.infer<typeof UpdateStripeCardVariablesSchema>;

export async function updateStripeCardFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateStripeCardVariables,
) {
  const instance = await axiosInstance;

  return instance.put(URL, UpdateStripeCardVariablesSchema.parse(variables));
}

export function useUpdateStripeCard() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateStripeCardVariables>({
    mutationFn: variables => updateStripeCardFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['paymentConfiguration'] });
      client.invalidateQueries({ queryKey: ['companyConfigurationV2'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateStripeCardVariables,
    'updateStripeCard'
  >('updateStripeCard', mutation);
}
